import { ERROR_MESSAGES } from "constant";
import moment from "moment";
import * as XLSX from "xlsx";

export const excelDownloadFile = (data, excelSheet, excelName) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, excelSheet);
  XLSX.writeFile(workbook, excelName);
};

export const parseTimestamp = (timestamp) => {
  if (!timestamp) {
    return null;
  }

  const milliseconds =
    timestamp.nanoseconds / 1000000 + timestamp.seconds * 1000;
  return milliseconds;
};

export const formatTimestamp = (timestamp) => {
  timestamp = moment(timestamp);
  return timestamp ? timestamp.format("DD/MM/YYYY h:mm A") : null;
};

export const calculateTotal = (amount, tax) => {
  const taxPercentage = tax / 100;
  const totalAmount = tax ? amount * (1 + taxPercentage) : amount;
  return Math.round(totalAmount * 100) / 100;
};

export const calculateTotalForSales = (numberArray) => {
  const sum = numberArray.reduce((accumulator, currentNumber) => {
    return accumulator + parseFloat(currentNumber);
  }, 0);

  return Math.round(sum);
};

export const getErrorMessage = (errorType, ...args) => {
  if (ERROR_MESSAGES[errorType]) {
    return ERROR_MESSAGES[errorType](...args);
  }
  return "Unknown error";
};

export const generateErrorResponse = (row, errors) => {
  return `Row ${row}: ${errors.join(", ")}`;
};

export const downloadErrorsAsTxt = (errors, file) => {
  const errorText = errors
    .map(
      (error) =>
        `Row ${error.row}, Field: ${error.field}, Message: ${error.message}`
    )
    .join("\n");
  const blob = new Blob([errorText], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = file;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const globaleDateTimeFormatter = (timestamp, type) => {
  if (!timestamp) {
    return false;
  }
  let dateTime;
  if (timestamp.nanoseconds) {
    const milliseconds =
      timestamp.nanoseconds / 1000000 + timestamp.seconds * 1000;
    dateTime = moment(milliseconds);
  } else {
    dateTime = moment(timestamp);
  }
  if (type === "date") {
    return dateTime ? dateTime.format("DD/MM/YYYY") : null;
  } else if (type === "time") {
    return dateTime ? dateTime.format("h:mm a") : null;
  }
  return dateTime ? dateTime.format("DD/MM/YYYY h:mm a") : null;
};

export const formatAnyDate = (input) => {
  if (!input) return "Invalid date";

  // Case 1: Firestore timestamp object
  if (
    typeof input === "object" &&
    input.seconds !== undefined &&
    input.nanoseconds !== undefined
  ) {
    const ms = input.seconds * 1000 + Math.floor(input.nanoseconds / 1e6);
    return moment(ms).format("DD/MM/YYYY HH:mm:ss");
  }

  // Case 2: ISO string or UNIX timestamp (string/number)
  const dateTime = moment(input);
  return dateTime.isValid()
    ? dateTime.format("DD/MM/YYYY HH:mm:ss")
    : "Invalid date";
};

export const unixTimeConverter = (date) => {
  let formattedDate;

  if (date.seconds) {
    const milliseconds = date.seconds * 1000;
    formattedDate = moment(milliseconds).format("DD/MM/YYYY h:mm a");
  } else {
    formattedDate = moment(date, "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)").format(
      "DD/MM/YYYY h:mm a"
    );
  }

  return formattedDate;
};

export const updateFirstLetter = (str) => {
  if (typeof str !== "string" || str.trim() === "") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getFormattedClientName = (client) => {
  const name =
    typeof client === "string" ? client : client?.value || client?.label || "";
  return updateFirstLetter(name.trim());
};

export const headerDesc = (mode, header, selectedItem) => {
  const title =
    (mode === "view" ? "" : selectedItem ? "Edit " : "Add ") + header;
  const description =
    mode === "view"
      ? "You can view details below."
      : `You can ${selectedItem ? "edit" : "add"} details below.`;
  return { title, description };
};

/**
 * Customer portal URL. Prepends CRA `homepage` / `PUBLIC_URL` (e.g. `/fitify`) so it matches `Router basename`.
 */
export const getUserLink = (uid, clientId) => {
  if (uid == null || clientId == null) return "";
  const baseUrl = window.location.origin;
  const raw = process.env.PUBLIC_URL || "";
  const prefix =
    raw === "/" ? "" : raw.endsWith("/") ? raw.slice(0, -1) : raw;
  return `${baseUrl}${prefix}/customer/${uid}/${clientId}`;
};

export const stripHtml = (html) => {
  if (!html) return "N/A";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "N/A";
};

export const sortByDate = (array) => {
  let sortedArray = [...array];
  sortedArray.sort((a, b) => {
    // Convert createdAt to Date objects
    const dateA = new Date(
      a.createdAt.seconds * 1000 + a.createdAt.nanoseconds / 1000000
    );
    const dateB = new Date(
      b.createdAt.seconds * 1000 + b.createdAt.nanoseconds / 1000000
    );
    // Compare dates
    return dateB - dateA;
  });
  return sortedArray;
};
